import * as dicomParser from "dicom-parser";
import * as cornerstone from "cornerstone-core";
import * as cornerstoneWADOImageLoader from "cornerstone-wado-image-loader";

/**
 * Cornerstone 초기화
 */
export const initializeCornerstone = () => {
  cornerstoneWADOImageLoader.external.cornerstone = cornerstone;
  cornerstoneWADOImageLoader.external.dicomParser = dicomParser;

  cornerstoneWADOImageLoader.configure({
    useWebWorkers: false,
  });
};

/**
 * Cornerstone 엘리먼트 활성화
 * @param {HTMLElement} element - Cornerstone를 활성화할 DOM 엘리먼트
 * @returns {boolean} - 활성화 성공 여부
 */
export const enableCornerstoneElement = (element) => {
  if (!element) {
    return false;
  }

  try {
    cornerstone.enable(element);
    return true;
  } catch (error) {
    console.error("Cornerstone 엘리먼트 활성화 실패:", error);
    return false;
  }
};

/**
 * Cornerstone 엘리먼트 비활성화
 * @param {HTMLElement} element - 비활성화할 DOM 엘리먼트
 */
export const disableCornerstoneElement = (element) => {
  if (!element) {
    return;
  }

  try {
    cornerstone.disable(element);
  } catch (error) {
    // 이미 비활성화되어 있으면 무시
  }
};

/**
 * Cornerstone 리셋
 * @param {HTMLElement} element - 리셋할 DOM 엘리먼트
 */
export const resetCornerstoneElement = (element) => {
  if (!element) {
    return;
  }

  try {
    cornerstone.reset(element);
  } catch (error) {
    // 무시
  }
};

/**
 * 파일 매니저 정리
 */
export const purgeFileManager = () => {
  try {
    cornerstoneWADOImageLoader.wadouri.fileManager.purge();
  } catch (error) {
    // 무시
  }
};

/**
 * DICOM 파일 로드 및 표시
 * @param {File} file - DICOM 파일
 * @param {HTMLElement} element - 이미지를 표시할 DOM 엘리먼트
 * @returns {Promise<Object>} - { image, viewport, jpgDataUrl }
 */
export const loadDicomFile = async (file, element) => {
  if (!file) {
    throw new Error("파일이 제공되지 않았습니다.");
  }

  if (!element) {
    throw new Error("엘리먼트가 제공되지 않았습니다.");
  }

  // 해당 element의 이전 이미지만 정리
  try {
    const enabledElement = cornerstone.getEnabledElement(element);
    if (enabledElement) {
      cornerstone.reset(element);
    }
  } catch (error) {
    // element가 활성화되지 않았거나 이미지가 없으면 무시
  }

  // element가 활성화되어 있는지 확인하고, 활성화되지 않았으면 활성화
  try {
    cornerstone.getEnabledElement(element);
  } catch (error) {
    // element가 활성화되지 않았으면 활성화
    cornerstone.enable(element);
  }

  // 파일 등록 및 imageId 생성
  // 각 섹션마다 고유한 파일로 등록하기 위해 element ID를 사용하여 고유성 보장
  const elementId = element.id || `element-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  const uniqueId = `${elementId}-${Date.now()}`;
  const imageId = cornerstoneWADOImageLoader.wadouri.fileManager.add(file, uniqueId);

  // 이미지 로드
  const image = await cornerstone.loadImage(imageId);

  // 기본 뷰포트 생성 (element가 활성화되어 있어야 함)
  const viewport = cornerstone.getDefaultViewportForImage(element, image);

  // 이미지 표시
  cornerstone.displayImage(element, image, viewport);

  // 이미지가 완전히 렌더링될 때까지 대기 (최대 3초)
  let renderComplete = false;
  let canvas = null;
  
  // element가 화면에 보이는지 확인
  const elementVisible = element.offsetWidth > 0 && element.offsetHeight > 0;
  
  for (let i = 0; i < 60; i++) {
    await new Promise((resolve) => setTimeout(resolve, 50));
    try {
      const enabledElement = cornerstone.getEnabledElement(element);
      canvas = enabledElement?.canvas;
      if (canvas && canvas.width > 0 && canvas.height > 0) {
        // Canvas에 실제로 데이터가 있는지 확인
        const testCtx = canvas.getContext("2d");
        const testImageData = testCtx.getImageData(0, 0, Math.min(10, canvas.width), Math.min(10, canvas.height));
        const hasContent = testImageData.data.some((val, idx) => idx % 4 !== 3 && val > 0);
        if (hasContent) {
          renderComplete = true;
          break;
        }
      }
    } catch (e) {
      // 계속 대기
    }
  }
  
  if (!renderComplete) {
    console.error("[dicomConverter] ⚠️ 렌더링 완료되지 않음 (60번 시도 후)", {
      canvasExists: !!canvas,
      canvasWidth: canvas?.width,
      canvasHeight: canvas?.height
    });
  }
  
  // 이미지 추출을 위해 추가 대기 (렌더링 안정화)
  if (renderComplete) {
    await new Promise((resolve) => setTimeout(resolve, 200));
  }

  // Canvas에서 JPG 데이터 추출 (렌더링 완료 후 즉시 추출)
  let jpgDataUrl = null;
  try {
    const enabledElement = cornerstone.getEnabledElement(element);
    const canvasElement = enabledElement?.canvas || canvas;
    
    if (canvasElement && canvasElement.width > 0 && canvasElement.height > 0) {
      // 렌더링된 Canvas에서 직접 추출
      try {
        // Canvas가 실제로 렌더링되었는지 확인
        const testCtx = canvasElement.getContext("2d");
        const testImageData = testCtx.getImageData(0, 0, Math.min(10, canvasElement.width), Math.min(10, canvasElement.height));
        const hasContent = testImageData.data.some((val, idx) => idx % 4 !== 3 && val > 0);
        
        if (hasContent && renderComplete) {
          // Canvas에서 직접 추출
          jpgDataUrl = canvasElement.toDataURL("image/jpeg", 0.95);
        } else {
          throw new Error("Canvas not ready, using pixel data directly");
        }
      } catch (canvasError) {
        // Canvas가 비어있거나 오류가 발생하면 이미지 픽셀 데이터를 직접 사용
        const tempCanvas = document.createElement("canvas");
        tempCanvas.width = image.width;
        tempCanvas.height = image.height;
        const ctx = tempCanvas.getContext("2d");
        
        const pixelData = image.getPixelData();
        const imageData = ctx.createImageData(image.width, image.height);
        
        // Window/Level 정보 가져오기
        const windowCenter = viewport.voi.windowCenter || 128;
        const windowWidth = viewport.voi.windowWidth || 256;
        const minValue = windowCenter - windowWidth / 2;
        const maxValue = windowCenter + windowWidth / 2;
        const range = maxValue - minValue || 1;
        
        // 픽셀 데이터를 0-255 범위로 정규화하여 RGBA 형식으로 변환
        for (let i = 0; i < pixelData.length; i++) {
          let pixelValue = pixelData[i];
          
          // Window/Level 적용
          pixelValue = Math.max(minValue, Math.min(maxValue, pixelValue));
          const normalizedValue = Math.round(((pixelValue - minValue) / range) * 255);
          
          imageData.data[i * 4] = normalizedValue;     // R
          imageData.data[i * 4 + 1] = normalizedValue; // G
          imageData.data[i * 4 + 2] = normalizedValue; // B
          imageData.data[i * 4 + 3] = 255;            // A
        }
        
        ctx.putImageData(imageData, 0, 0);
        jpgDataUrl = tempCanvas.toDataURL("image/jpeg", 0.95);
      }
    } else {
      // Canvas가 없으면 픽셀 데이터를 직접 사용
      const tempCanvas = document.createElement("canvas");
      tempCanvas.width = image.width;
      tempCanvas.height = image.height;
      const ctx = tempCanvas.getContext("2d");
      
      const pixelData = image.getPixelData();
      const imageData = ctx.createImageData(image.width, image.height);
      
      // Window/Level 정보 가져오기
      const windowCenter = viewport.voi.windowCenter || 128;
      const windowWidth = viewport.voi.windowWidth || 256;
      const minValue = windowCenter - windowWidth / 2;
      const maxValue = windowCenter + windowWidth / 2;
      const range = maxValue - minValue || 1;
      
      // 픽셀 데이터를 0-255 범위로 정규화하여 RGBA 형식으로 변환
      for (let i = 0; i < pixelData.length; i++) {
        let pixelValue = pixelData[i];
        
        // Window/Level 적용
        pixelValue = Math.max(minValue, Math.min(maxValue, pixelValue));
        const normalizedValue = Math.round(((pixelValue - minValue) / range) * 255);
        
        imageData.data[i * 4] = normalizedValue;     // R
        imageData.data[i * 4 + 1] = normalizedValue; // G
        imageData.data[i * 4 + 2] = normalizedValue; // B
        imageData.data[i * 4 + 3] = 255;            // A
      }
      
      ctx.putImageData(imageData, 0, 0);
      jpgDataUrl = tempCanvas.toDataURL("image/jpeg", 0.95);
    }
  } catch (error) {
    console.error("[dicomConverter] ❌ Canvas → JPG 변환 중 오류:", error);
    throw error;
  }
  
  if (!jpgDataUrl) {
    console.error("[dicomConverter] ❌ jpgDataUrl이 null입니다!");
    throw new Error("이미지 변환 실패: JPG 데이터를 생성할 수 없습니다.");
  }

  // 이미지 추출 완료 후 즉시 Cornerstone element 완전히 비활성화 (계속 렌더링 방지)
  try {
    // 먼저 reset
    cornerstone.reset(element);
    // 그 다음 완전히 disable
    cornerstone.disable(element);
    // element를 숨김으로써 추가 렌더링 방지
    if (element) {
      element.style.display = "none";
      element.style.visibility = "hidden";
      element.style.width = "0";
      element.style.height = "0";
    }
  } catch (e) {
    console.error("[dicomConverter] ⚠️ Cornerstone 비활성화 중 오류:", e);
  }

  return {
    image,
    viewport,
    jpgDataUrl,
  };
};

/**
 * DICOM 파일 유효성 검사
 * @param {File} file - 검사할 파일
 * @returns {boolean} - DICOM 파일 여부
 */
export const isValidDicomFile = (file) => {
  if (!file) {
    return false;
  }

  const fileName = file.name.toLowerCase();
  const fileType = file.type;

  return (
    fileName.endsWith(".dcm") ||
    fileName.endsWith(".dicom") ||
    fileType === "application/dicom" ||
    fileType === "application/dicom+json"
  );
};

/**
 * DataURL을 JPG File 객체로 변환
 * @param {string} dataUrl - 변환할 dataURL (예: "data:image/jpeg;base64,...")
 * @param {string} fileName - 생성할 파일명 (기본값: "image.jpg")
 * @returns {File} - JPG File 객체
 */
export const dataUrlToJpgFile = (dataUrl, fileName = "image.jpg") => {
  if (!dataUrl) {
    throw new Error("DataURL이 제공되지 않았습니다.");
  }

  const arr = dataUrl.split(",");
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], fileName, { type: mime || "image/jpeg" });
};

